// Centralized email service for all contact forms and subscriptions
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyXaPIXLeJYfnnsLbvW8ZSlvM81ZpPEoX8ZXOmi0pLwnJaK3WhpjjLuxD5pOVXukC8hfA/exec';
const RECIPIENT_EMAIL = 'communitysupport@petition.io';

/**
 * Send email through Google Apps Script
 * @param {Object} params - Email parameters
 * @param {string} params.userName - User's name
 * @param {string} params.userEmail - User's email address
 * @param {string} params.subject - Email subject line
 * @param {string} params.message - Email message body
 * @returns {Promise<boolean>} - Success status
 */
export const sendEmail = async ({ userName, userEmail, subject, message }) => {
  try {
    // eslint-disable-next-line no-unused-vars
    const response = await fetch(GOOGLE_SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: RECIPIENT_EMAIL,
        from: userEmail,
        name: userName,
        subject: subject,
        message: message
      })
    });

    // Note: no-cors mode doesn't allow reading response, so we assume success
    return true;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

/**
 * Send subscription notification for DAO page
 * @param {string} userName - User's name
 * @param {string} userEmail - User's email address
 * @returns {Promise<boolean>}
 */
export const subscribeToDAO = async (userName, userEmail) => {
  return sendEmail({
    userName: userName,
    userEmail: userEmail,
    subject: `DAO Subscription Request from ${userName}`,
    message: `DAO SUBSCRIPTION REQUEST

User Details:
Name: ${userName}
Email: ${userEmail}
Timestamp: ${new Date().toLocaleString()}

Request Type: Petitioner DAO Launch Notification

The user wants to be notified when the Petitioner DAO launches.

Action Required: Add ${userEmail} to the DAO notification list.`
  });
};

/**
 * Send subscription notification for Impact Showcase page
 * @param {string} userName - User's name
 * @param {string} userEmail - User's email address
 * @returns {Promise<boolean>}
 */
export const subscribeToImpact = async (userName, userEmail) => {
  return sendEmail({
    userName: userName,
    userEmail: userEmail,
    subject: `Impact Showcase Subscription from ${userName}`,
    message: `IMPACT SHOWCASE SUBSCRIPTION REQUEST

User Details:
Name: ${userName}
Email: ${userEmail}
Timestamp: ${new Date().toLocaleString()}

Request Type: Impact Stories Updates

The user wants to receive updates about impact stories and success cases.

Action Required: Add ${userEmail} to the Impact Showcase notification list.`
  });
};

/**
 * Send subscription notification for Merch Store page
 * @param {string} userName - User's name
 * @param {string} userEmail - User's email address
 * @returns {Promise<boolean>}
 */
export const subscribeToMerch = async (userName, userEmail) => {
  return sendEmail({
    userName: userName,
    userEmail: userEmail,
    subject: `Merch Store Subscription from ${userName}`,
    message: `MERCH STORE SUBSCRIPTION REQUEST

User Details:
Name: ${userName}
Email: ${userEmail}
Timestamp: ${new Date().toLocaleString()}

Request Type: Merch Store Launch Notification

The user wants to be notified when the Merch Store launches.

Action Required: Add ${userEmail} to the Merch Store notification list.`
  });
};

/**
 * Send contact form submission
 * @param {Object} formData - Contact form data
 * @param {string} formData.name - User's name
 * @param {string} formData.email - User's email
 * @param {string} formData.subject - Message subject
 * @param {string} formData.message - Message content
 * @returns {Promise<boolean>}
 */
export const sendContactForm = async (formData) => {
  return sendEmail({
    userName: formData.name,
    userEmail: formData.email,
    subject: `Contact Form: ${formData.subject}`,
    message: `CONTACT FORM SUBMISSION

User Details:
Name: ${formData.name}
Email: ${formData.email}
Subject: ${formData.subject}
Timestamp: ${new Date().toLocaleString()}

Message:
${formData.message}

---
Reply to: ${formData.email}`
  });
};

/**
 * Send a bug report with optional base64-encoded screenshot attachment
 * @param {Object} reportData - Bug report data
 * @param {string} reportData.name - Reporter's name
 * @param {string} reportData.email - Reporter's email
 * @param {string} reportData.category - Bug category
 * @param {string} reportData.description - Bug description
 * @param {string} reportData.steps - Steps to reproduce
 * @param {string} reportData.expected - Expected behavior
 * @param {string} reportData.actual - Actual behavior
 * @param {string|null} reportData.attachmentBase64 - Base64-encoded screenshot (optional)
 * @param {string|null} reportData.attachmentName - Screenshot filename (optional)
 * @returns {Promise<boolean>}
 */
export const sendBugReport = async (reportData) => {
  const attachmentSection = reportData.attachmentBase64
    ? `\n\nSCREENSHOT ATTACHED: ${reportData.attachmentName}\n[Base64 data included — see raw payload]\nData: ${reportData.attachmentBase64}`
    : '\n\nNo screenshot attached.';

  return sendEmail({
    userName: reportData.name,
    userEmail: reportData.email,
    subject: `[Bug Report] ${reportData.category} — app.petition.io`,
    message: `BUG REPORT — app.petition.io dApp

Reporter Details:
Name: ${reportData.name}
Email: ${reportData.email}
Timestamp: ${new Date().toLocaleString()}

Bug Category: ${reportData.category}

Description:
${reportData.description}

Steps to Reproduce:
${reportData.steps}

Expected Behavior:
${reportData.expected}

Actual Behavior:
${reportData.actual}
${attachmentSection}

---
Reply to: ${reportData.email}`
  });
};

const emailService = {
  sendEmail,
  subscribeToDAO,
  subscribeToImpact,
  subscribeToMerch,
  sendContactForm,
  sendBugReport
};

export default emailService;
